/**
 * Manejo único de conversión monetaria del PMIS.
 *
 * # Modelo conceptual
 *
 * El PMIS opera con dos valores por cada entidad transaccional:
 *
 *   - `monto` + `moneda`  → valor en la moneda contractual (la que se firmó).
 *                           Es la etiqueta para display y el dato legal.
 *   - `montoUsd`, `montoCop` → valores congelados al momento de crear/editar
 *                              la entidad, usando la TRM del AFE padre como
 *                              `trmCongelada`.
 *
 * Toda comparación cruzada entre entidades (techo de coste, saldos, KPIs)
 * usa `montoUsd` como unidad canónica. Esto elimina conversiones runtime y
 * garantiza simetría entre el consumo y la liberación: el USD comprometido al
 * crear una SO es exactamente el USD que se libera al cancelarla, sin
 * reconvertir con TRMs distintas.
 *
 * # Diferencia en cambio
 *
 * El pago efectivo se contabiliza por separado:
 *
 *   `montoPagadoUsd     = valorPago / trmPago`   (si moneda !== "USD")
 *   `diferenciaCambioUsd = montoPagadoUsd − montoUsd`
 *
 * Esta diferencia es resultado financiero (P&L), NO afecta el techo del AFE.
 */

/** Resultado de congelar un monto con la TRM del AFE. */
export interface MontoCongelado {
  montoUsd: number;
  montoCop: number;
  trmCongelada: number;
}

/**
 * Congela un monto contractual a sus equivalentes USD y COP usando la TRM
 * del AFE padre. Esta función es la **única** fuente de conversión en el
 * sistema; cualquier otro lugar que necesite convertir debe llamarla aquí.
 *
 * @param monto Valor en la moneda contractual.
 * @param moneda Moneda contractual de la entidad.
 * @param trmAfe TRM congelada del AFE padre (COP por 1 USD).
 *
 * Si `trmAfe` no es válida (≤0), se devuelve `monto` tal cual en ambos
 * lados; eso permite seedear datos antes de tener TRM. No es lo deseable
 * en producción pero evita crashes en desarrollo.
 */
export function congelarMonto(
  monto: number,
  moneda: "USD" | "COP",
  trmAfe: number,
): MontoCongelado {
  if (trmAfe <= 0) {
    return { montoUsd: monto, montoCop: monto, trmCongelada: trmAfe };
  }
  if (moneda === "USD") {
    return {
      montoUsd: round2(monto),
      montoCop: Math.round(monto * trmAfe),
      trmCongelada: trmAfe,
    };
  }
  // moneda === "COP"
  return {
    montoUsd: round2(monto / trmAfe),
    montoCop: Math.round(monto),
    trmCongelada: trmAfe,
  };
}

/**
 * Calcula la contabilidad del pago: cuánto USD se giró efectivamente y cuál
 * es la diferencia respecto al commitment.
 *
 *   `montoPagadoUsd     = (moneda === "USD") ? valorPago : valorPago / trmPago`
 *   `diferenciaCambioUsd = montoPagadoUsd − montoUsd`
 *
 * Positivo = pérdida FX (la moneda local se valorizó frente al USD).
 * Negativo = ganancia FX.
 *
 * Solo aplica si la entidad NO es USD; para entidades en USD no hay efecto
 * cambiario por definición y se devuelve `diferenciaCambioUsd = 0`.
 */
export function calcularDiferenciaCambio(args: {
  monedaEntidad: "USD" | "COP";
  valorPago: number;
  trmPago: number;
  montoUsdCommitment: number;
}): { montoPagadoUsd: number; diferenciaCambioUsd: number } {
  const { monedaEntidad, valorPago, trmPago, montoUsdCommitment } = args;
  if (monedaEntidad === "USD") {
    return {
      montoPagadoUsd: round2(valorPago),
      diferenciaCambioUsd: round2(valorPago - montoUsdCommitment),
    };
  }
  if (trmPago <= 0) {
    return { montoPagadoUsd: 0, diferenciaCambioUsd: 0 };
  }
  const montoPagadoUsd = round2(valorPago / trmPago);
  return {
    montoPagadoUsd,
    diferenciaCambioUsd: round2(montoPagadoUsd - montoUsdCommitment),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
